import {
  ShiftManagementBulkDeleteRestDaysDocument,
  ShiftManagementBulkDeleteShiftsDocument,
} from 'generated/resources/graphql'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useMutation } from 'modules/core/hooks/useMutation'
import { i18nDateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { useRefreshStats } from 'modules/shift_management/hooks/refreshShiftsRows'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { trackDeleteByRangeEvent } from 'modules/shift_management/lib/trackDeleteEvent'
import RestDay from 'modules/shift_management/store/internal/models/RestDay'
import Shift from 'modules/shift_management/store/internal/models/Shift'
import { EmployeesEmployeeSelectorInput } from 'generated/resources/graphql'

interface Props {
  startDate: Date
  endDate: Date
  employeeSelection: EmployeesEmployeeSelectorInput
  destroyBackupShifts: boolean
  destroyAutomaticOnly: boolean
  onClose: () => void
}

const useDestroyByRange = ({
  startDate,
  endDate,
  employeeSelection,
  destroyBackupShifts,
  destroyAutomaticOnly,
  onClose
}: Props) => {
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const [{ view }] = useSchedule()
  const { refreshStats } = useRefreshStats()
  const restDayFlag = hasFeatureFlag('shift_management_rest_day')

  const startDateParam = i18nDateToString(startDate, 'backend')
  const endDateParam = i18nDateToString(endDate, 'backend')

  const [destroyShifts] = useMutation(ShiftManagementBulkDeleteShiftsDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkDeleteShiftmanagementShift,
  })

  const [destroyRestDays] = useMutation(ShiftManagementBulkDeleteRestDaysDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkDeleteShiftmanagementRestDay,
  })

  const { running: submitting, performAction } = useAsyncAction(
    async () => {
      const result = await destroyShifts({
        variables: {
          startAt: startDateParam,
          endAt: endDateParam,
          employeeSelection,
          destroyBackupShifts: destroyBackupShifts,
          destroyAutomaticShifts: destroyAutomaticOnly,
        },
        update: (cache, { data }) => {
          data?.shiftManagementMutations.bulkDeleteShiftmanagementShift.shifts?.forEach((shift) => {
            cache.evict({
              id: cache.identify(shift),
            })
          })
        },
      })

      const handled = handleResult(result, (_errors) => {})

      const shiftsData = handled.success ? handled.data?.shifts || [] : []
      const deletedShifts = shiftsData.map((shift) =>
        Shift.buildFromGraphQL({
          shift,
          employeeFullName: shift.employee?.fullName || '',
          employeeAvatar: shift.employee?.avatar?.url || '',
          employeeLocationId: shift.employee?.locationId || 0,
        })
      )

      const deleteRestDays = async () => {
        if (!restDayFlag) return []
        const restDaysResult = await destroyRestDays({
          variables: {
            startAt: startDateParam,
            endAt: endDateParam,
            employeeSelection,
          },
          update: (cache, { data }) => {
            data?.shiftManagementMutations.bulkDeleteShiftmanagementRestDay.restDays?.forEach(
              (restDay) => {
                cache.evict({
                  id: cache.identify(restDay),
                })
              }
            )
          },
        })

        const handledRestDays = handleResult(restDaysResult, (_errors) => {})
        const restDays = handledRestDays.success ? handledRestDays.data?.restDays || [] : []
        return restDays.map((restDay) => {
          return RestDay.build({
            id: restDay.id,
            employee_id: restDay.employee?.id || 0,
            date: restDay.date,
            state: restDay.state,
            timezone: restDay.timezone || '',
            employeeFullName: restDay.employee?.fullName || '',
            employeeAvatar: restDay.employee?.avatar?.url || '',
          })
        })
      }

      const deletedRestDays = await deleteRestDays()

      dispatchShiftsRows({ type: 'DESTROY_SHIFTS', shifts: deletedShifts, view })
      dispatchShiftsRows({ type: 'DESTROY_REST_DAYS', restDays: deletedRestDays })

      trackDeleteByRangeEvent(
        deletedShifts,
        deletedRestDays,
        view,
        startDateParam,
        endDateParam,
        destroyAutomaticOnly
      )

      await refreshStats(shiftsRows)

      onClose()
    },
    {
      errorFlash: true,
      successMessage: i18n.t('shift_management.actions.delete_shifts_success'),
    }
  )

  return {
    onBulkDestroyShifts: performAction,
    submitting,
  }
}

export default useDestroyByRange
